/**
 * Owns: Find My Store modal (search, selection UI, cart sync, header triggers, PDP inventory badges).
 * Exposes: store-locator custom element, findAlternateDeliveryStores, selectStoreForPdp,
 *   renderAlternateDeliveryResults, openFromTrigger.
 * Consumes: store-locator-search, store-locator-geocoding, store-locator-cart, store-locator-pdp,
 *   store-inventory, store-pricing, store-locator-places-autocomplete.
 */

console.log('[store-locator] build marker 2026-07-22');

import { Component } from '@theme/component';
import { DialogCloseEvent, DialogComponent } from '@theme/dialog';
import { CartUpdateEvent, ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { fetchConfig, refreshHeaderLayout, syncHeaderHeightVars } from '@theme/utilities';
import {
  GOOGLE_MAPS_API_KEY_FALLBACK,
  classifyQuery,
  createGeocodingClient,
  formatDistanceMiles,
  isMapsApiAuthFailed,
  isStreetLevelGeocode,
} from '@theme/store-locator-geocoding';
import { createPlacesAutocompleteClient } from '@theme/store-locator-places-autocomplete';
import {
  allowsContinueSellingAtStore,
  availabilityToBadgeState,
  getPickUpEnabledForLocation,
  getStoreAvailabilityEntryForLocation,
  getStoreInventoryConfig,
  isStoreInventoryMockEnabled,
  parseStoreLocatorAvailabilities,
  readContinueSellingSignalsFromElement,
  resolveStoreAvailability,
  resolveStoreAvailabilityFromShopifyEntry,
  resolveStoreInventoryForLocation,
} from '@theme/store-inventory';
import {
  buildCartAttributes,
  buildEmptyStoreCartAttributes,
  findStoreFromCartAttributes,
  hasStoreCartAttributes,
  normalizeCartAttributes,
  normalizeLocationId,
  normalizeServiceZip,
  STORE_LOCATOR_SOURCE,
  STORE_SELECTED_EVENT,
  storeIdsMatch,
} from '@theme/store-pricing';
import {
  filterStoresByServiceZip,
  findAlternateDeliveryStores as findAlternateDeliveryStoresFromCatalog,
  enrichOriginWithPostalCode,
  geocodeToSearchOrigin,
  geocodeZipOrigin,
  getMaxDistanceMilesForRadius,
  getZipListKeyForRadius,
  RADIUS_TEN_MILES,
  isStateOnlyCityStateSearch,
  rankStoresByDistance,
  resolveCityStateSearchCandidates,
  shouldApplyDistanceCap,
} from '@theme/store-locator-search';
import {
  dispatchStoreLocatorCartUpdate,
  fetchCartJson,
  patchCartAttributes as patchStoreCartAttributes,
} from '@theme/store-locator-cart';
import { findPdpStoreLocatorTrigger, isStoreInventoryPdpTrigger } from '@theme/store-locator-pdp';

/**
 * Store locator — source of truth and pipeline
 *
 * Source of truth: cart.attributes only (store_location_id, shopify_location_id,
 * store_code, store_zip). No localStorage for selection.
 *
 * Embedded catalog: store_location metaobjects as JSON in <template> chunks on this
 * element. Used to resolve display/address/ZIP search, not persistence.
 *
 * Search eligibility: ZIP and city/state queries use zipsByRadius metaobject lists (per radius
 * tier) once the query is geocoded to a service ZIP. State-only queries match catalog address
 * fields. City/state without a resolvable ZIP falls back to address-city matching plus a
 * haversine distance cap. Store-name search bypasses ZIP lists (explicit store intent).
 *
 * Pipeline: load config → load catalog → fetch cart → resolve selection → #state → render UI.
 *
 * Stale cart: if cart has store attrs, catalog loaded (length > 0), and no index match,
 * PATCH cart to clear store attrs, then selectedStore = null and render default.
 * If catalog is empty, never PATCH-clear cart (UI shows default only).
 */

/**
 * @typedef {Object} CartUpdateResult
 * @property {boolean} ok
 * @property {Object} [cart]
 * @property {string} [error]
 */

/**
 * @typedef {Object} StoreLocatorAddress
 * @property {string} [address1]
 * @property {string} [address2]
 * @property {string} [city]
 * @property {string} [state]
 * @property {string} [zipcode]
 */

/**
 * @typedef {Object} StoreLocatorStore
 * @property {string} id
 * @property {string} handle
 * @property {string} displayName
 * @property {string} [storeCode]
 * @property {string} [shopifyLocationId]
 * @property {string} [phone]
 * @property {boolean} [enableStorePricing]
 * @property {string[]} [hours]
 * @property {StoreLocatorAddress} address
 * @property {{ lat: number; lng: number }} [coordinates]
 * @property {Record<string, string[]>} zipsByRadius
 */

/**
 * @typedef {Object} SelectedStore
 * @property {string} id
 * @property {string} handle
 * @property {string} displayName
 * @property {string} [shopifyLocationId]
 * @property {string} [storeCode]
 * @property {string} [zipcode]
 * @property {string} [address1]
 */

/**
 * @typedef {Object} StoreLocatorAvailability
 * @property {boolean} available
 * @property {boolean} pickUpEnabled
 */

/**
 * @typedef {Object} StoreLocatorPickupInfo
 * @property {boolean} pickUpEnabled
 */

/**
 * @typedef {Object} StoreLocatorBadgeState
 * @property {boolean} inStock
 * @property {boolean} pickup
 * @property {import('@theme/store-inventory').StoreInventoryStatus} [status]
 * @property {boolean} [pickUpEnabled]
 */

/**
 * @typedef {Object} StoreLocatorPdpContext
 * @property {string} variantId
 * @property {string} productUrl
 * @property {string} sectionId
 * @property {boolean} storeInventoryEnabled
 * @property {string} inventoryApiBase
 * @property {string} inventoryApiAuthToken
 * @property {number} inventoryThreshold
 * @property {Record<string, StoreLocatorPickupInfo>} pickUpByLocationId
 * @property {Record<string, StoreLocatorAvailability>} availabilitiesByLocationId
 * @property {string} inventoryPolicy
 * @property {string[]} inStockInStoreValues
 */

/**
 * @typedef {Object} StoreLocatorSearchOrigin
 * @property {number} lat
 * @property {number} lng
 * @property {string} displayZip
 * @property {string} originLabel
 */

/**
 * @typedef {Object} StoreLocatorState
 * @property {{ radiiMiles: string[]; defaultRadius: string }} config
 * @property {StoreLocatorStore[]} catalog
 * @property {Record<string, string> | null} cartAttributes
 * @property {SelectedStore | null} selectedStore
 * @property {{ query: string; zip: string; radius: string; results: StoreLocatorStore[]; uiState: 'idle'|'results'|'resultsMyStoreOnly'|'noResults'; noResultsZip: string; noResultsRadius: string; noResultsMode: 'zip'|'cityState'|'state'; origin: StoreLocatorSearchOrigin | null; distancesByStoreId: Record<string, number> }} search
 * @property {StoreLocatorPdpContext | null} pdpContext
 * @property {{ cartSyncGeneration: number }} meta
 */

/**
 * @param {HTMLElement} host
 * @returns {HTMLTemplateElement[]}
 */
function getDataChunks(host) {
  return Array.from(host.querySelectorAll('template[data-store-locator-data-chunk]')).filter(
    (element) => element instanceof HTMLTemplateElement
  );
}

/**
 * @param {StoreLocatorStore[]} stores
 * @returns {StoreLocatorStore[]}
 */
function prepareStores(stores) {
  return stores.map((store) => {
    const zipsByRadius = /** @type {Record<string, string[]>} */ ({});

    for (const [mile, zips] of Object.entries(store.zipsByRadius ?? {})) {
      const key = String(mile).trim();
      if (!key) continue;

      zipsByRadius[key] = Array.isArray(zips)
        ? zips
            .map((entry) => normalizeServiceZip(String(entry)))
            .filter((zip) => zip.length === 5)
        : [];
    }

    const hours = Array.isArray(store.hours)
      ? store.hours.map((line) => String(line).trim()).filter(Boolean)
      : [];

    return { ...store, hours, zipsByRadius };
  });
}

/**
 * @param {HTMLElement} host
 * @returns {StoreLocatorStore[]}
 */
function loadCatalogFromHost(host) {
  const chunks = getDataChunks(host);

  if (chunks.length === 0) {
    console.warn('[store-locator] No store data chunks found on #store-locator-root');
    return [];
  }

  /** @type {StoreLocatorStore[]} */
  const merged = [];

  for (const [index, chunk] of chunks.entries()) {
    const text = chunk.content.textContent?.trim() ?? '';
    if (!text) continue;

    try {
      const parsed = JSON.parse(text);
      if (Array.isArray(parsed)) merged.push(...parsed);
    } catch (error) {
      console.error(`[store-locator] JSON parse error (chunk ${index})`, error);
    }
  }

  return prepareStores(merged);
}

/**
 * @param {HTMLElement} host
 * @returns {{ radiiMiles: string[]; defaultRadius: string }}
 */
function loadConfigFromHost(host) {
  const radiiAttr = host.dataset.storeLocatorRadii ?? '';
  const radiiMiles = radiiAttr
    .split(',')
    .map((value) => value.trim())
    .filter(Boolean);

  const defaultRadius = host.dataset.storeLocatorDefaultRadius?.trim() || '50';

  return { radiiMiles, defaultRadius };
}

/**
 * @param {HTMLElement} host
 * @returns {string}
 */
function loadGoogleApiKeyFromHost(host) {
  return host.dataset.storeLocatorGoogleApiKey?.trim() || GOOGLE_MAPS_API_KEY_FALLBACK;
}

/**
 * @param {StoreLocatorSearchOrigin | null} origin
 * @param {string} [searchQuery]
 * @returns {string}
 */
function getOriginLabelForDisplay(origin, searchQuery = '') {
  if (!origin) return searchQuery.trim();

  return (
    origin.originLabel?.trim() ||
    origin.displayZip?.trim() ||
    searchQuery.trim()
  );
}

/**
 * @param {StoreLocatorStore} store
 * @returns {SelectedStore}
 */
function catalogStoreToSelectedStore(store) {
  return {
    id: store.id,
    handle: store.handle,
    displayName: store.displayName || store.handle,
    shopifyLocationId: store.shopifyLocationId ?? '',
    storeCode: store.storeCode ?? '',
    zipcode: store.address?.zipcode ?? '',
    address1: store.address?.address1 ?? '',
  };
}

/**
 * @param {StoreLocatorStore[]} catalog
 * @param {Record<string, string> | null | undefined} cartAttributes
 * @returns {SelectedStore | null}
 */
function resolveSelectedStore(catalog, cartAttributes) {
  if (!hasStoreCartAttributes(cartAttributes ?? {})) return null;

  const store = findStoreFromCartAttributes(
    /** @type {import('@theme/store-pricing').StoreCatalogEntry[]} */ (catalog),
    /** @type {Record<string, string>} */ (cartAttributes)
  );
  return store ? catalogStoreToSelectedStore(/** @type {StoreLocatorStore} */ (store)) : null;
}

/**
 * @returns {StoreLocatorState}
 */
function createInitialState() {
  return {
    config: { radiiMiles: [], defaultRadius: '50' },
    catalog: [],
    cartAttributes: null,
    selectedStore: null,
    search: {
      query: '',
      zip: '',
      radius: '',
      results: [],
      uiState: 'idle',
      noResultsZip: '',
      noResultsRadius: '',
      noResultsMode: 'zip',
      origin: null,
      distancesByStoreId: {},
    },
    pdpContext: null,
    meta: { cartSyncGeneration: 0 },
  };
}

/**
 * Find My Store modal and ZIP/radius search.
 *
 * @extends {Component}
 */
class StoreLocator extends Component {
  /** @type {StoreLocatorState} */
  #state = createInitialState();

  /** @type {boolean} */
  #selectInProgress = false;

  /** @type {boolean} */
  #searchInProgress = false;

  /** @type {number} */
  #blockResultSelectionUntil = 0;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #resultSelectionGuardTimer = null;

  /** @type {ReturnType<typeof createGeocodingClient> | null} */
  #geocoding = null;

  /** @type {ReturnType<typeof createPlacesAutocompleteClient> | null} */
  #placesAutocomplete = null;

  /** @type {string} */
  #autocompleteSessionToken = '';

  /** @type {ReturnType<typeof setTimeout> | null} */
  #autocompleteDebounceTimer = null;

  /** @type {AbortController | null} */
  #autocompleteFetchAbort = null;

  /** @type {{ text: string; placeId: string }[]} */
  #autocompleteSuggestions = [];

  /** @type {number} */
  #autocompleteActiveIndex = -1;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #autocompleteBlurTimer = null;

  /** @type {MutationObserver | null} */
  #headerMutationObserver = null;

  /** @type {Map<string, StoreLocatorBadgeState>} */
  #inventoryBadgeCache = new Map();

  /** @type {number} */
  #inventoryRequestGeneration = 0;

  /** @type {AbortController | null} */
  #inventoryFetchAbort = null;

  #controller = new AbortController();

connectedCallback() {
  super.connectedCallback();

  if (this.#controller.signal.aborted) {
    this.#controller = new AbortController();
  }

  const { signal } = this.#controller;

    this.addEventListener('click', this.#handleResultsClick, { signal });
    this.addEventListener('click', this.#handleRadiusOptionClick, { signal });
    document.addEventListener('click', this.#handleStorePageClick, { signal });

    const searchForm = this.querySelector('[data-store-locator-search]');
    if (searchForm instanceof HTMLFormElement) {
      searchForm.addEventListener(
        'submit',
        (event) => {
          this.handleSearchSubmit(/** @type {SubmitEvent} */ (event));
        },
        { signal }
      );
    }

    const searchSubmit = this.querySelector('[data-store-locator-submit]');
    if (searchSubmit instanceof HTMLButtonElement) {
      searchSubmit.addEventListener(
        'click',
        (event) => {
          event.preventDefault();
          void this.#runSearch();
        },
        { signal }
      );
    }

    this.#setupQueryAutocomplete();

    this.#setupDialogOpenGuard();

    this.dialog?.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });

    document.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, { signal });
    document.addEventListener(STORE_SELECTED_EVENT, this.#handleStoreSelected, { signal });

    void this.#initialize();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#headerMutationObserver?.disconnect();
    this.#headerMutationObserver = null;
    if (this.#resultSelectionGuardTimer !== null) {
      clearTimeout(this.#resultSelectionGuardTimer);
      this.#resultSelectionGuardTimer = null;
    }
    if (this.#autocompleteDebounceTimer !== null) {
      clearTimeout(this.#autocompleteDebounceTimer);
      this.#autocompleteDebounceTimer = null;
    }
    if (this.#autocompleteBlurTimer !== null) {
      clearTimeout(this.#autocompleteBlurTimer);
      this.#autocompleteBlurTimer = null;
    }
    this.#autocompleteFetchAbort?.abort();
    this.#autocompleteFetchAbort = null;
    this.#controller.abort();
  }

  #setupQueryAutocomplete() {
    const apiKey = loadGoogleApiKeyFromHost(this);
    if (!apiKey) return;

    const queryInput = this.querySelector('[data-store-locator-query]');
    const listEl = this.querySelector('[data-store-locator-autocomplete]');
    if (!(queryInput instanceof HTMLInputElement) || !(listEl instanceof HTMLUListElement)) return;

    this.#placesAutocomplete = createPlacesAutocompleteClient(apiKey);
    this.#autocompleteSessionToken = this.#placesAutocomplete.createSessionToken();

    const { signal } = this.#controller;
    const noResultsText =
      Theme.translations?.store_locator_autocomplete_no_results ?? 'No matching addresses';

    queryInput.addEventListener(
      'input',
      () => {
        if (this.#autocompleteDebounceTimer !== null) {
          clearTimeout(this.#autocompleteDebounceTimer);
        }
        this.#autocompleteDebounceTimer = setTimeout(() => {
          this.#autocompleteDebounceTimer = null;
          void this.#fetchAndRenderAutocomplete(queryInput, listEl, noResultsText);
        }, 300);
      },
      { signal }
    );

    queryInput.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Escape') {
          if (!listEl.hidden) {
            event.preventDefault();
            this.#hideAutocomplete(queryInput, listEl);
          }
          return;
        }

        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          if (listEl.hidden || this.#autocompleteSuggestions.length === 0) return;
          event.preventDefault();
          const delta = event.key === 'ArrowDown' ? 1 : -1;
          const next =
            this.#autocompleteActiveIndex < 0
              ? event.key === 'ArrowDown'
                ? 0
                : this.#autocompleteSuggestions.length - 1
              : (this.#autocompleteActiveIndex + delta + this.#autocompleteSuggestions.length) %
                this.#autocompleteSuggestions.length;
          this.#setAutocompleteActiveIndex(queryInput, listEl, next);
          return;
        }

        if (event.key !== 'Enter') return;

        if (!listEl.hidden && this.#autocompleteActiveIndex >= 0) {
          event.preventDefault();
          void this.#selectAutocompleteSuggestion(queryInput, listEl, this.#autocompleteActiveIndex);
          return;
        }

        event.preventDefault();
        this.#hideAutocomplete(queryInput, listEl);
        void this.#runSearch();
      },
      { signal }
    );

    queryInput.addEventListener(
      'blur',
      () => {
        if (this.#autocompleteBlurTimer !== null) {
          clearTimeout(this.#autocompleteBlurTimer);
        }
        this.#autocompleteBlurTimer = setTimeout(() => {
          this.#autocompleteBlurTimer = null;
          this.#hideAutocomplete(queryInput, listEl);
        }, 150);
      },
      { signal }
    );

    listEl.addEventListener(
      'mousedown',
      (event) => {
        const option = event.target instanceof Element ? event.target.closest('[role="option"]') : null;
        if (!option) return;
        event.preventDefault();
        const index = Number(option.getAttribute('data-index'));
        if (!Number.isFinite(index)) return;
        void this.#selectAutocompleteSuggestion(queryInput, listEl, index);
      },
      { signal }
    );
  }

  /**
   * @param {HTMLInputElement} queryInput
   * @param {HTMLUListElement} listEl
   * @param {string} noResultsText
   */
  async #fetchAndRenderAutocomplete(queryInput, listEl, noResultsText) {
    if (this.#searchInProgress || !this.#placesAutocomplete) {
      this.#hideAutocomplete(queryInput, listEl);
      return;
    }

    const value = queryInput.value.trim();
    if (value.length < 2) {
      this.#hideAutocomplete(queryInput, listEl);
      return;
    }

    this.#autocompleteFetchAbort?.abort();
    const fetchAbort = new AbortController();
    this.#autocompleteFetchAbort = fetchAbort;

    const suggestions = await this.#placesAutocomplete.fetchSuggestions(
      value,
      this.#autocompleteSessionToken,
      { signal: fetchAbort.signal }
    );

    if (fetchAbort.signal.aborted) return;

    this.#autocompleteSuggestions = suggestions;
    this.#autocompleteActiveIndex = -1;
    listEl.replaceChildren();

    if (suggestions.length === 0) {
      const empty = document.createElement('li');
      empty.className = 'store-locator-modal__autocomplete-empty';
      empty.setAttribute('role', 'presentation');
      empty.textContent = noResultsText;
      listEl.append(empty);
      this.#showAutocomplete(queryInput, listEl);
      queryInput.setAttribute('aria-activedescendant', '');
      return;
    }

    for (let index = 0; index < suggestions.length; index += 1) {
      const suggestion = suggestions[index];
      if (!suggestion) continue;
      const item = document.createElement('li');
      item.setAttribute('role', 'presentation');

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'store-locator-modal__autocomplete-option';
      button.setAttribute('role', 'option');
      button.id = `store-locator-autocomplete-option-${index}`;
      button.setAttribute('data-index', String(index));
      button.setAttribute('aria-selected', 'false');
      button.textContent = suggestion.text;

      item.append(button);
      listEl.append(item);
    }

    this.#showAutocomplete(queryInput, listEl);
    queryInput.setAttribute('aria-activedescendant', '');
  }

  /**
   * @param {HTMLInputElement} queryInput
   * @param {HTMLUListElement} listEl
   * @param {number} index
   */
  #setAutocompleteActiveIndex(queryInput, listEl, index) {
    const options = listEl.querySelectorAll('[role="option"]');
    for (const option of options) {
      if (!(option instanceof HTMLElement)) continue;
      option.setAttribute('aria-selected', 'false');
    }

    const active = options[index];
    if (!(active instanceof HTMLElement)) return;

    this.#autocompleteActiveIndex = index;
    active.setAttribute('aria-selected', 'true');
    queryInput.setAttribute('aria-activedescendant', active.id);
    active.scrollIntoView({ block: 'nearest' });
  }

  /**
   * @param {HTMLInputElement} queryInput
   * @param {HTMLUListElement} listEl
   */
  #showAutocomplete(queryInput, listEl) {
    listEl.hidden = false;
    queryInput.setAttribute('aria-expanded', 'true');
  }

  /**
   * @param {HTMLInputElement} queryInput
   * @param {HTMLUListElement} listEl
   */
  #hideAutocomplete(queryInput, listEl) {
    this.#autocompleteFetchAbort?.abort();
    this.#autocompleteFetchAbort = null;
    this.#autocompleteSuggestions = [];
    this.#autocompleteActiveIndex = -1;
    listEl.hidden = true;
    listEl.replaceChildren();
    queryInput.setAttribute('aria-expanded', 'false');
    queryInput.setAttribute('aria-activedescendant', '');
  }

  /**
   * @param {HTMLInputElement} queryInput
   * @param {HTMLUListElement} listEl
   * @param {number} index
   */
  async #selectAutocompleteSuggestion(queryInput, listEl, index) {
    if (this.#autocompleteBlurTimer !== null) {
      clearTimeout(this.#autocompleteBlurTimer);
      this.#autocompleteBlurTimer = null;
    }

    const suggestion = this.#autocompleteSuggestions[index];
    if (!suggestion) return;

    queryInput.value = suggestion.text;
    this.#hideAutocomplete(queryInput, listEl);

    if (this.#placesAutocomplete) {
      this.#autocompleteSessionToken = this.#placesAutocomplete.createSessionToken();
    }

    void this.#runSearch();
  }

  #setupDialogOpenGuard() {
    const dialog = this.dialog;
    if (!dialog || dialog.dataset.storeLocatorOpenGuard === 'true') return;

    dialog.dataset.storeLocatorOpenGuard = 'true';
    let openedAt = 0;
    const originalShow = dialog.showDialog.bind(dialog);
    const originalClose = dialog.closeDialog.bind(dialog);

    dialog.showDialog = () => {
      openedAt = Date.now();
      if (!this.#state.pdpContext) {
        const trigger = this.#findPdpTrigger();
        if (trigger instanceof HTMLElement) {
          this.#state.pdpContext = this.#parsePdpContextFromTrigger(trigger);
        }
      }
      const result = originalShow();
      this.#refreshPdpBadgesOnModalOpen();
      return result;
    };

    dialog.closeDialog = async () => {
      if (Date.now() - openedAt < 400 && !this.#selectInProgress) {
        return;
      }

      return originalClose();
    };
  }

  /**
   * @param {number} [untilTimestamp]
   */
  #armResultSelectionGuard(untilTimestamp = Date.now() + 450) {
    this.#blockResultSelectionUntil = untilTimestamp;
    this.#updateResultListPointerEvents();

    if (this.#resultSelectionGuardTimer !== null) {
      clearTimeout(this.#resultSelectionGuardTimer);
    }

    const delay = Math.max(0, untilTimestamp - Date.now());
    this.#resultSelectionGuardTimer = setTimeout(() => {
      this.#resultSelectionGuardTimer = null;
      this.#updateResultListPointerEvents();
    }, delay);
  }

  #updateResultListPointerEvents() {
    const blocked = Date.now() < this.#blockResultSelectionUntil;

    for (const refName of ['resultsList', 'selectedList']) {
      const list = this.#getRef(refName);
      if (list instanceof HTMLElement) {
        list.style.pointerEvents = blocked ? 'none' : '';
      }
    }
  }

  /**
   * @returns {DialogComponent | null}
   */
  get dialog() {
    return this.querySelector('dialog-component');
  }

  /**
   * @param {string} name
   * @returns {Element | null}
   */
  #getRef(name) {
    return this.querySelector(`[ref="${name}"]`);
  }

  async #initialize() {
    try {
      this.#state.config = loadConfigFromHost(this);
      this.#state.catalog = loadCatalogFromHost(this);

      const apiKey = loadGoogleApiKeyFromHost(this);
      if (!apiKey) {
        console.warn('[store-locator] Google Maps API key is not configured in theme settings');
        this.#showError(Theme.translations.store_locator_api_key_missing ?? '');
      }

      this.#geocoding = createGeocodingClient(apiKey);
      await this.#hydrateSelectionFromCart();
      this.#renderUi();
      this.#watchHeaderStoreLocator();
      this.dataset.storeLocatorReady = 'true';
    } catch (error) {
      console.error('[store-locator] initialize failed', error);
    }
  }

  /**
   * @returns {boolean}
   */
  #headerDomMatchesState() {
    const trigger = document.querySelector('[data-store-locator-header]');
    if (!(trigger instanceof HTMLElement)) return true;

    const selectedBlock = trigger.querySelector('.store-locator__selected');
    const shouldShowSelected = Boolean(this.#state.selectedStore?.displayName);
    const isShowingSelected =
      selectedBlock instanceof HTMLElement && !selectedBlock.hidden;

    return shouldShowSelected === isShowingSelected;
  }

  #watchHeaderStoreLocator() {
    const root = document.querySelector('[data-store-locator-header]')?.closest('.store-locator');
    if (!(root instanceof HTMLElement)) return;

    this.#headerMutationObserver?.disconnect();
    this.#headerMutationObserver = new MutationObserver(() => {
      if (this.#headerDomMatchesState()) return;
      this.#renderUi();
    });
    this.#headerMutationObserver.observe(root, {
      attributes: true,
      childList: true,
      subtree: true,
      attributeFilter: ['hidden', 'class'],
    });
  }

  #scheduleHeaderUiRefresh() {
    this.#renderUi({ refreshHeader: false });
    requestAnimationFrame(() => {
      syncHeaderHeightVars();
      refreshHeaderLayout();
    });
  }

  /**
   * Opens the modal after reading PDP context from the triggering element.
   * @param {Event} event
   */
  openFromTrigger = (event) => {
    event.preventDefault();

    const trigger = event.target instanceof Element ? event.target.closest('[data-store-locator-trigger]') : null;

    if (trigger instanceof HTMLElement) {
      this.#state.pdpContext = this.#parsePdpContextFromTrigger(trigger);
    } else {
      this.#state.pdpContext = null;
    }

    this.dialog?.showDialog();
  };

  /**
   * Opens the locator with a prefilled search query (e.g. invalid delivery ZIP handoff).
   * @param {string} query
   * @param {{ runSearch?: boolean }} [options]
   */
  openWithQuery(query, options = {}) {
    const { runSearch = true } = options;
    const trimmed = String(query ?? '').trim();

    const queryInput = this.querySelector('[data-store-locator-query]');
    if (queryInput instanceof HTMLInputElement) {
      queryInput.value = trimmed;
    }

    this.#ensurePdpContext();

    this.dialog?.showDialog();

    if (runSearch && trimmed) {
      void this.#runSearch();
    }
  }

  /**
   * Nearby stores that deliver to `zip` with in-stock inventory (PDP delivery modal).
   * @param {string} zip
   * @param {{ signal?: AbortSignal; limit?: number }} [options]
   * @returns {Promise<{
   *   stores: StoreLocatorStore[];
   *   distancesByStoreId: Record<string, number>;
   *   origin: StoreLocatorSearchOrigin | null;
   * }>}
   */
  async findAlternateDeliveryStores(zip, options = {}) {
    const { signal, limit = 5 } = options;

    this.#ensurePdpContext();

    const geocoding = this.#geocoding;
    const pdpContext = this.#state.pdpContext;

    if (!geocoding || this.#state.catalog.length === 0 || !pdpContext || !this.#usesInventoryBadges()) {
      return { stores: [], distancesByStoreId: {}, origin: null };
    }

    try {
      return /** @type {{ stores: StoreLocatorStore[]; distancesByStoreId: Record<string, number>; origin: StoreLocatorSearchOrigin | null }} */ (
        await findAlternateDeliveryStoresFromCatalog({
          catalog: this.#state.catalog,
          zip,
          defaultRadius: this.#state.config.defaultRadius,
          geocoding,
          pdpContext,
          excludeStoreId: this.#state.selectedStore?.id ?? null,
          signal,
          limit,
        })
      );
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw error;
      }
      console.warn('[store-locator] findAlternateDeliveryStores failed', error);
      return { stores: [], distancesByStoreId: {}, origin: null };
    }
  }

  /**
   * Sets the customer's store from PDP contexts without opening the locator modal.
   * @param {StoreLocatorStore} store
   * @returns {Promise<{ ok: boolean }>}
   */
  async selectStoreForPdp(store) {
    if (Date.now() < this.#blockResultSelectionUntil) {
      return { ok: false };
    }

    if (this.#selectInProgress) {
      return { ok: false };
    }

    this.#selectInProgress = true;

    try {
      const requestedAttributes = buildCartAttributes(store);
      const result = await this.#patchCartAttributes(requestedAttributes, { dispatchCartUpdate: false });

      if (!result.ok) {
        return { ok: false };
      }

      const cart = /** @type {{ attributes?: Record<string, unknown> } | undefined} */ (result.cart);
      this.#state.cartAttributes = normalizeCartAttributes(cart?.attributes);
      this.#state.selectedStore = resolveSelectedStore(this.#state.catalog, this.#state.cartAttributes);

      if (!this.#state.selectedStore) {
        console.warn('[store-locator] selectStoreForPdp: cart updated but store not resolved', {
          requestedAttributes,
          store,
        });
        return { ok: false };
      }

      this.#state.meta.cartSyncGeneration++;
      this.#hideError();

      this.#dispatchCartUpdateEvent(/** @type {{ item_count?: number }} */ (cart));

      document.dispatchEvent(
        new CustomEvent(STORE_SELECTED_EVENT, {
          bubbles: true,
          detail: { store },
        })
      );

      this.#scheduleHeaderUiRefresh();

      return { ok: true };
    } finally {
      this.#selectInProgress = false;
    }
  }

  #ensurePdpContext() {
    if (this.#state.pdpContext) return;

    const pdpTrigger = this.#findPdpTrigger();
    if (pdpTrigger instanceof HTMLElement) {
      this.#state.pdpContext = this.#parsePdpContextFromTrigger(pdpTrigger);
    }
  }

  /**
   * Builds store locator result cards for the delivery ZIP modal (reuses result template + badges).
   * @param {StoreLocatorStore[]} stores
   * @param {{
   *   distancesByStoreId?: Record<string, number>;
   *   origin?: StoreLocatorSearchOrigin | null;
   *   searchQuery?: string;
   *   onSelectStore?: (store: StoreLocatorStore, button: HTMLButtonElement) => void;
   * }} [options]
   * @returns {HTMLElement[]}
   */
  renderAlternateDeliveryResults(stores, options = {}) {
    this.#ensurePdpContext();

    const resultTemplate = this.#getRef('resultTemplate');
    if (!(resultTemplate instanceof HTMLTemplateElement)) {
      return [];
    }

    const {
      distancesByStoreId = {},
      origin = null,
      searchQuery = '',
      onSelectStore,
    } = options;

    /** @type {HTMLElement[]} */
    const items = [];

    for (const store of stores) {
      const fragment = /** @type {DocumentFragment} */ (resultTemplate.content.cloneNode(true));
      const item = fragment.querySelector('.store-locator-modal__result');
      if (!(item instanceof HTMLElement)) continue;

      this.#populateResultItem(item, store, {
        distancesByStoreId,
        origin,
        searchQuery,
        onSelectStore,
      });
      items.push(item);
    }

    if (stores.length > 0 && this.#usesInventoryBadges()) {
      void this.#hydrateResultBadges(stores);
    }

    return items;
  }

  /**
   * @param {HTMLElement} trigger
   * @returns {StoreLocatorPdpContext | null}
   */
  #parsePdpContextFromTrigger(trigger) {
    const variantId = trigger.dataset.variantId?.trim();
    const productUrl = trigger.dataset.productUrl?.trim();
    const sectionId = trigger.dataset.sectionId?.trim();

    if (!variantId || !productUrl || !sectionId) return null;

    /** @type {Record<string, StoreLocatorAvailability>} */
    const availabilitiesByLocationId = {};
    /** @type {Record<string, StoreLocatorPickupInfo>} */
    const pickUpByLocationId = {};

    const parsedAvailabilities = parseStoreLocatorAvailabilities(trigger.dataset.storeLocatorAvailabilities);
    Object.assign(availabilitiesByLocationId, parsedAvailabilities.availabilitiesByLocationId);
    Object.assign(pickUpByLocationId, parsedAvailabilities.pickUpByLocationId);

    const {
      baseUrl: inventoryApiBase,
      authToken: inventoryApiAuthToken,
      threshold: inventoryThreshold,
    } = getStoreInventoryConfig(trigger);
    const storeInventoryEnabled = trigger.dataset.storeInventoryEnabled === 'true';
    const { inventoryPolicy, inStockInStoreValues } = readContinueSellingSignalsFromElement(trigger);

    return {
      variantId,
      productUrl,
      sectionId,
      storeInventoryEnabled,
      inventoryApiBase,
      inventoryApiAuthToken,
      inventoryThreshold,
      pickUpByLocationId,
      availabilitiesByLocationId,
      inventoryPolicy,
      inStockInStoreValues,
    };
  }

  /**
   * @returns {boolean}
   */
  #usesInventoryBadges() {
    const ctx = this.#state.pdpContext;
    if (!ctx?.storeInventoryEnabled || !ctx.variantId) return false;
    return Boolean(ctx.inventoryApiBase.trim() || isStoreInventoryMockEnabled());
  }

  /**
   * @param {StoreLocatorStore} store
   * @param {StoreLocatorPdpContext} pdpContext
   * @returns {boolean}
   */
  #getPickUpEnabled(store, pdpContext) {
    return getPickUpEnabledForLocation(store.shopifyLocationId, pdpContext.pickUpByLocationId);
  }

  /**
   * @param {StoreLocatorStore} store
   * @returns {StoreLocatorAvailability | null}
   */
  #getLegacyAvailabilityForStore(store) {
    const pdpContext = this.#state.pdpContext;
    if (!pdpContext || !store.shopifyLocationId) return null;

    const shopifyId = String(store.shopifyLocationId).trim();
    const normalized = normalizeLocationId(shopifyId);

    const byLocation = pdpContext.availabilitiesByLocationId;
    return (
      byLocation[shopifyId] ??
      byLocation[normalized] ??
      Object.entries(byLocation).find(([key]) => normalizeLocationId(key) === normalized)?.[1] ??
      null
    );
  }

  /**
   * Sync badge state from Shopify variant availabilities (before API hydrate).
   * @param {StoreLocatorStore} store
   * @returns {import('@theme/store-inventory').StoreLocatorBadgeState | null}
   */
  #resolveBadgeStateForStore(store) {
    const pdpContext = this.#state.pdpContext;
    if (!pdpContext) return null;

    const pickUpEnabled = this.#getPickUpEnabled(store, pdpContext);
    const threshold = pdpContext.inventoryThreshold;
    const locationId = store.shopifyLocationId?.trim();

    if (!locationId) {
      return availabilityToBadgeState(resolveStoreAvailability(null, threshold, pickUpEnabled));
    }

    const entry = getStoreAvailabilityEntryForLocation(locationId, pdpContext.availabilitiesByLocationId);
    if (entry) {
      return availabilityToBadgeState(resolveStoreAvailabilityFromShopifyEntry(entry));
    }

    return availabilityToBadgeState(resolveStoreAvailability(null, threshold, pickUpEnabled));
  }

  /**
   * @param {string[]} storeIds
   * @param {boolean} isHydrating
   */
  #setBadgesHydrating(storeIds, isHydrating) {
    for (const storeId of storeIds) {
      for (const refName of ['resultsList', 'selectedList']) {
        const list = this.#getRef(refName);
        if (!(list instanceof HTMLElement)) continue;

        const item = list.querySelector(`.store-locator-modal__result[data-store-id="${CSS.escape(storeId)}"]`);
        const badgesWrapper = item?.querySelector('.store-locator-modal__result-badges');
        if (badgesWrapper instanceof HTMLElement) {
          badgesWrapper.setAttribute('aria-busy', isHydrating ? 'true' : 'false');
        }
      }
    }
  }

#handleRadiusOptionClick = (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const option = target.closest('[data-store-locator-radius-option]');
  if (!(option instanceof HTMLButtonElement)) return;

  const value = option.dataset.value?.trim();
  if (!value) return;

  event.preventDefault();
  this.selectDistance(value, event);
};

/** @param {MouseEvent} event */
#handleStorePageClick = (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const button = target.closest('[data-store-page-set-store]');
  if (!(button instanceof HTMLButtonElement)) return;

  event.preventDefault();

  console.log('[store-locator] Store page button clicked', {
    button,
    storeId: button.dataset.storeId
  });

  const shopifyLocationId = button.dataset.storeId?.trim();

  if (!shopifyLocationId) {
    console.warn(
      '[store-locator] Store page button is missing its location ID'
    );
    return;
  }

  const normalizedPageLocationId =
    normalizeLocationId(shopifyLocationId);

  const store = this.#state.catalog.find((entry) => {
    const catalogLocationId =
      String(entry.shopifyLocationId ?? '').trim();

    return (
      catalogLocationId &&
      normalizeLocationId(catalogLocationId) ===
        normalizedPageLocationId
    );
  });

  console.log('[store-locator] Store page catalog match', {
    shopifyLocationId,
    normalizedPageLocationId,
    store
  });

  if (!store) {
    console.warn(
      '[store-locator] Store page location was not found in catalog',
      {
        shopifyLocationId,
        normalizedPageLocationId
      }
    );

    return;
  }

  this.#selectStore(store, button).catch((error) => {
    console.error(
      '[store-locator] Store page selection failed',
      error
    );
  });
};

#handleResultsClick = (event) => {
    if (Date.now() < this.#blockResultSelectionUntil) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    const target = event.target;
    if (!(target instanceof Element)) return;

    const button = target.closest('[data-store-locator-set-store]');
    if (!(button instanceof HTMLButtonElement)) return;

    const result = button.closest('.store-locator-modal__result');
    if (!(result instanceof HTMLElement)) return;

    const storeId = result.dataset.storeId?.trim();
    if (!storeId) return;

    const store = this.#state.search.results.find((entry) => String(entry.id) === storeId);
    if (!store) return;

    event.preventDefault();
    void this.#selectStore(store, button);
  };

  handleDistanceToggle = () => {
    const details = this.#getRef('distanceDetails');
    if (!(details instanceof HTMLDetailsElement)) return;

    const summary = details.querySelector('summary');
    summary?.setAttribute('aria-expanded', details.open ? 'true' : 'false');
  };

  /**
   * @param {number | string | { distance?: number | string }} distanceOrData
   * @param {Event} event
   */
  selectDistance = (distanceOrData, event) => {
    event.preventDefault();

    const rawDistance =
      typeof distanceOrData === 'object' && distanceOrData !== null && 'distance' in distanceOrData
        ? distanceOrData.distance
        : distanceOrData;
    const value = String(rawDistance ?? '').trim();
    const label = `${value} mi.`;
    const distanceDetails = this.#getRef('distanceDetails');
    const distanceValue = this.#getRef('distanceValue');
    const distanceInput = this.#getRef('distanceInput');

    if (distanceInput instanceof HTMLInputElement) {
      distanceInput.value = value;
    }

    if (distanceValue instanceof HTMLElement) {
      distanceValue.textContent = label;
    }

    if (distanceDetails instanceof HTMLDetailsElement) {
      distanceDetails.querySelectorAll('[data-store-locator-radius-option]').forEach((option) => {
        const isSelected = option instanceof HTMLElement && option.dataset.value === value;
        option.setAttribute('aria-selected', isSelected ? 'true' : 'false');
      });

      distanceDetails.open = false;
      const summary = distanceDetails.querySelector('summary');
      summary?.setAttribute('aria-expanded', 'false');
    }
  };

  /** @param {SubmitEvent} event */
  handleSearchSubmit = (event) => {
    event.preventDefault();
    void this.#runSearch();
  };

  /** @param {Event} event */
  handleCurrentLocation = async (event) => {
    event.preventDefault();

    if (this.#searchInProgress) return;

    if (!navigator.geolocation) {
      this.#showError(Theme.translations.store_locator_geolocation_unavailable ?? '');
      return;
    }

    this.#hideError();

    try {
      const position = await this.#getCurrentPosition();
      const geocoding = this.#geocoding;

      if (!geocoding) {
        this.#showError(this.#getGeocodingErrorMessage());
        return;
      }

      const geocode = await geocoding.reverseGeocode(position.coords.latitude, position.coords.longitude);

      if (!geocode) {
        this.#showError(this.#getGeocodingErrorMessage());
        return;
      }

      const zip = normalizeServiceZip(geocode.postalCode ?? '');
      const queryInput = this.#getRef('queryInput');

      if (queryInput instanceof HTMLInputElement && zip) {
        queryInput.value = zip;
      }

      const origin = geocodeToSearchOrigin(geocode, zip, queryInput instanceof HTMLInputElement ? queryInput.value.trim() : '');
      await this.#runSearch({ origin });
    } catch (error) {
      const geoError = /** @type {GeolocationPositionError | undefined} */ (error);
      if (geoError?.code === 1) {
        this.#showError(Theme.translations.store_locator_geolocation_denied ?? '');
        return;
      }

      if (geoError?.code === 3) {
        this.#showError(Theme.translations.store_locator_geolocation_timeout ?? '');
        return;
      }

      this.#showError(this.#getGeocodingErrorMessage());
    }
  };

  #hideQueryAutocomplete() {
    const queryInput = this.#getRef('queryInput');
    const listEl = this.querySelector('[data-store-locator-autocomplete]');
    if (queryInput instanceof HTMLInputElement && listEl instanceof HTMLUListElement) {
      this.#hideAutocomplete(queryInput, listEl);
    }
  }

  /**
   * @param {{ origin?: StoreLocatorSearchOrigin }} [options]
   */
  async #runSearch(/** @type {{ origin?: StoreLocatorSearchOrigin }} */ options = {}) {
    if (this.#searchInProgress) return;

    this.#hideQueryAutocomplete();
    this.#searchInProgress = true;
    this.#setSearchLoading(true);
    this.#hideError();

    try {
      if (!loadGoogleApiKeyFromHost(this)) {
        this.#showError(Theme.translations.store_locator_api_key_missing ?? '');
        this.#state.search.uiState = 'idle';
        return;
      }

      const geocoding = this.#geocoding;
      if (!geocoding) {
        console.warn('[store-locator] geocoding client not initialized');
        this.#showError(this.#getGeocodingErrorMessage());
        this.#state.search.uiState = 'idle';
        return;
      }

      const queryInput = this.#getRef('queryInput');
      const distanceInput = this.#getRef('distanceInput');
      const query = queryInput instanceof HTMLInputElement ? queryInput.value.trim() : '';

      if (!query) {
        this.#showError(Theme.translations.store_locator_invalid_query ?? '');
        this.#state.search.uiState = 'idle';
        return;
      }

      const radius =
        distanceInput instanceof HTMLInputElement
          ? distanceInput.value.trim()
          : this.#state.config.defaultRadius;

      if (!this.#state.config.radiiMiles.includes(radius)) {
        console.warn('[store-locator] invalid search radius', { radius, radii: this.#state.config.radiiMiles });
        this.#showError(Theme.translations.store_locator_geocoding_failed ?? '');
        this.#state.search.uiState = 'idle';
        return;
      }

      const radiusMiles = Number.parseFloat(radius);
      if (!Number.isFinite(radiusMiles)) {
        this.#state.search.uiState = 'idle';
        return;
      }

      if (this.#state.catalog.length === 0) {
        console.warn('[store-locator] catalog is empty; store data may have failed to load');
        this.#showError(Theme.translations.store_locator_geocoding_failed ?? '');
        this.#state.search.uiState = 'idle';
        return;
      }

      const classified = classifyQuery(query, this.#state.catalog);

      if (classified.type === 'empty') {
        this.#showError(Theme.translations.store_locator_invalid_query ?? '');
        this.#state.search.uiState = 'idle';
        return;
      }

      /** @type {StoreLocatorStore[]} */
      let candidates = [];
      /** @type {StoreLocatorSearchOrigin | null} */
      let origin = options.origin ?? null;
      /** @type {'zip' | 'location' | 'cityState' | 'storeName' | 'empty'} */
      let searchMode = classified.type;
      /** @type {'zip' | 'cityState' | 'state'} */
      let noResultsMode = 'zip';
      /** @type {{ city?: string; state?: string }} */
      let cityStateFilters = {};
      const zipListKey = getZipListKeyForRadius(radius);
      const maxDistanceMiles = getMaxDistanceMilesForRadius(radius, radiusMiles);

      if (classified.type === 'zip') {
        const zip = classified.zip;
        if (!origin) {
          origin = await geocodeZipOrigin(geocoding, zip, query);
          if (!origin) {
            this.#showError(this.#getGeocodingErrorMessage());
            this.#state.search.uiState = 'idle';
            return;
          }
        }

        candidates = /** @type {StoreLocatorStore[]} */ (
          filterStoresByServiceZip(this.#state.catalog, zip, zipListKey)
        );
      } else if (classified.type === 'storeName') {
        candidates = /** @type {StoreLocatorStore[]} */ (classified.stores);
        origin = options.origin ?? this.#state.search.origin;
      } else if (classified.type === 'cityState') {
        cityStateFilters = { city: classified.city, state: classified.state };
        noResultsMode = isStateOnlyCityStateSearch(cityStateFilters) ? 'state' : 'cityState';

        if (!origin) {
          const geocodeQuery =
            [classified.city, classified.state].filter(Boolean).join(', ') || classified.query;
          const geocode = await geocoding.geocodeAddress(geocodeQuery);
          if (geocode) {
            if (geocode.city || geocode.state) {
              cityStateFilters = {
                city: geocode.city || cityStateFilters.city,
                state: geocode.state || cityStateFilters.state,
              };
              noResultsMode = isStateOnlyCityStateSearch(cityStateFilters) ? 'state' : 'cityState';
            }

            origin = geocodeToSearchOrigin(geocode, '', classified.query);
            origin = await enrichOriginWithPostalCode(geocoding, origin);
          }
        }

        const resolved = resolveCityStateSearchCandidates(this.#state.catalog, {
          cityStateFilters,
          origin,
          zipListKey,
        });
        candidates = /** @type {StoreLocatorStore[]} */ (resolved.candidates);
        searchMode = resolved.searchMode;
        noResultsMode = resolved.noResultsMode;
      } else {
        if (!origin) {
          const geocode = await geocoding.geocodeAddress(classified.query);
          if (!geocode) {
            this.#showError(this.#getGeocodingErrorMessage());
            this.#state.search.uiState = 'idle';
            return;
          }
          origin = geocodeToSearchOrigin(geocode, '', query);

          if (isStreetLevelGeocode(geocode.locationTypes ?? [], classified.query)) {
            const serviceZip = normalizeServiceZip(origin.displayZip ?? '');
            if (!serviceZip) {
              this.#showError(Theme.translations.store_locator_geocode_no_zip ?? '');
              this.#state.search.uiState = 'idle';
              return;
            }

            candidates = /** @type {StoreLocatorStore[]} */ (
              filterStoresByServiceZip(this.#state.catalog, serviceZip, zipListKey)
            );
          } else if (geocode.city || geocode.state) {
            cityStateFilters = { city: geocode.city, state: geocode.state };
            origin = await enrichOriginWithPostalCode(geocoding, origin);
            const resolved = resolveCityStateSearchCandidates(this.#state.catalog, {
              cityStateFilters,
              origin,
              zipListKey,
            });
            candidates = /** @type {StoreLocatorStore[]} */ (resolved.candidates);
            searchMode = resolved.searchMode;
            noResultsMode = resolved.noResultsMode;
          } else {
            const serviceZip = normalizeServiceZip(origin.displayZip ?? '');
            if (!serviceZip) {
              this.#showError(Theme.translations.store_locator_geocode_no_zip ?? '');
              this.#state.search.uiState = 'idle';
              return;
            }

            candidates = /** @type {StoreLocatorStore[]} */ (
              filterStoresByServiceZip(this.#state.catalog, serviceZip, zipListKey)
            );
          }
        } else {
          const serviceZip = normalizeServiceZip(origin.displayZip ?? '');
          if (!serviceZip) {
            this.#showError(Theme.translations.store_locator_geocode_no_zip ?? '');
            this.#state.search.uiState = 'idle';
            return;
          }

          candidates = /** @type {StoreLocatorStore[]} */ (
            filterStoresByServiceZip(this.#state.catalog, serviceZip, zipListKey)
          );
        }
      }

      const applyRadiusFilter = shouldApplyDistanceCap(searchMode, radius, cityStateFilters);

      const { ranked, distancesByStoreId } = await rankStoresByDistance({
        candidates,
        origin,
        applyRadiusFilter,
        maxDistanceMiles,
        classifiedType: searchMode,
        geocoding,
      });

      const matches = /** @type {StoreLocatorStore[]} */ (ranked.map((entry) => entry.store));
      const displayZip = origin?.displayZip ?? '';
      const selectedId = this.#state.selectedStore?.id ?? null;
      const selectedOnlyMatch =
        selectedId !== null &&
        matches.length > 0 &&
        matches.every((store) => storeIdsMatch(selectedId, store.id));

      this.#state.search.query = query;
      this.#state.search.zip = displayZip;
      this.#state.search.radius = radius;
      this.#state.search.origin = origin;
      this.#state.search.distancesByStoreId = distancesByStoreId;

      if (matches.length === 0) {
        const cityStateLabel =
          classified.type === 'cityState'
            ? [classified.city, classified.state].filter(Boolean).join(', ')
            : '';

        this.#state.search.results = [];
        this.#state.search.noResultsZip =
          noResultsMode === 'cityState' || noResultsMode === 'state'
            ? origin?.originLabel || cityStateLabel || query
            : displayZip || query;
        this.#state.search.noResultsRadius = radius;
        this.#state.search.noResultsMode = noResultsMode;
        this.#state.search.uiState = 'noResults';
        return;
      }

      this.#state.search.results = matches;
      this.#state.search.uiState = selectedOnlyMatch ? 'resultsMyStoreOnly' : 'results';
    } catch (error) {
      console.error('[store-locator] search failed', error);
      this.#showError(this.#getGeocodingErrorMessage());
      this.#state.search.uiState = 'idle';
    } finally {
      this.#searchInProgress = false;
      this.#setSearchLoading(false);
      this.#armResultSelectionGuard();
      this.#renderUi();
    }
  }

  /**
   * @returns {Promise<GeolocationPosition>}
   */
  #getCurrentPosition() {
    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, {
        enableHighAccuracy: false,
        timeout: 10000,
        maximumAge: 60000,
      });
    });
  }

  /**
   * @param {boolean} isLoading
   */
  #setSearchLoading(isLoading) {
    const searchRow = this.#getRef('searchRow');
    const searchSubmit = this.#getRef('searchSubmit');
    const currentLocationButton = this.#getRef('currentLocationButton');
    const searchLoading = this.#getRef('searchLoading');
    const emptyState = this.#getRef('emptyState');
    const selectedPanel = this.#getRef('selectedPanel');
    const resultsPanel = this.#getRef('resultsPanel');
    const noResultsPanel = this.#getRef('noResultsPanel');

    if (searchRow instanceof HTMLElement) {
      searchRow.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    }

    if (searchLoading instanceof HTMLElement) {
      searchLoading.hidden = !isLoading;
      searchLoading.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    }

    if (isLoading) {
      const selectLoading = this.#getRef('selectLoading');
      if (selectLoading instanceof HTMLElement) selectLoading.hidden = true;

      if (emptyState instanceof HTMLElement) emptyState.hidden = true;
      if (resultsPanel instanceof HTMLElement) resultsPanel.hidden = true;
      if (noResultsPanel instanceof HTMLElement) noResultsPanel.hidden = true;
    }

    if (searchSubmit instanceof HTMLButtonElement) {
      searchSubmit.disabled = isLoading;
    }

    if (currentLocationButton instanceof HTMLButtonElement) {
      currentLocationButton.disabled = isLoading;
    }
  }

  /**
   * @param {boolean} isLoading
   */
  #setSelectLoading(isLoading) {
    const selectLoading = this.#getRef('selectLoading');
    const searchLoading = this.#getRef('searchLoading');
    const modalContent = this.#getRef('modalContent');
    const emptyState = this.#getRef('emptyState');
    const resultsPanel = this.#getRef('resultsPanel');
    const noResultsPanel = this.#getRef('noResultsPanel');

    if (isLoading && searchLoading instanceof HTMLElement) {
      searchLoading.hidden = true;
    }

    if (selectLoading instanceof HTMLElement) {
      selectLoading.hidden = !isLoading;
      selectLoading.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    }

    if (modalContent instanceof HTMLElement) {
      modalContent.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    }

    if (isLoading) {
      if (emptyState instanceof HTMLElement) emptyState.hidden = true;
      if (resultsPanel instanceof HTMLElement) resultsPanel.hidden = true;
      if (noResultsPanel instanceof HTMLElement) noResultsPanel.hidden = true;
    }
  }

  #handleDialogClose = () => {
    this.#setSelectLoading(false);
    this.#inventoryFetchAbort?.abort();
    this.#inventoryFetchAbort = null;
    this.#inventoryBadgeCache.clear();
    this.#state.pdpContext = null;
    this.#resetSearch();
    this.#renderUi();
  };

  #handleStoreSelected = () => {
    this.#renderUi();
  };

  /**
   * @param {VariantUpdateEvent} event
   */
  #handleVariantUpdate = (event) => {
    const trigger = this.#findPdpTrigger();
    if (!trigger) return;

    const html = event.detail?.data?.html;
    if (html instanceof Document) {
      const updated = html.querySelector('[data-store-locator-trigger][data-store-locator-pdp]');
      if (updated instanceof HTMLElement) {
        this.#copyTriggerDataset(trigger, updated);
      }
    } else if (this.#state.pdpContext) {
      const variantId = event.detail?.resource?.id;
      if (variantId) {
        void this.#fetchTriggerAvailabilities(trigger, variantId);
      }
    }

    const previousVariantId = this.#state.pdpContext?.variantId;
    const nextPdpContext = this.#parsePdpContextFromTrigger(trigger);
    this.#state.pdpContext = nextPdpContext;

    if (previousVariantId !== nextPdpContext?.variantId) {
      this.#inventoryBadgeCache.clear();
    }

    const resultsPanel = this.#getRef('resultsPanel');
    if (resultsPanel instanceof HTMLElement && !resultsPanel.hidden && this.#state.search.results.length > 0) {
      this.#renderResults(this.#state.search.results);
    }

    void this.#hydrateVisibleResultBadges();
  };

  /**
   * @param {HTMLElement} from
   * @param {HTMLElement} to
   */
  #copyTriggerDataset(from, to) {
    for (const name of [
      'variantId',
      'productUrl',
      'sectionId',
      'storeLocatorAvailabilities',
      'storeInventoryEnabled',
      'inventoryApiBase',
      'inventoryApiAuthToken',
      'inventoryThreshold',
    ]) {
      const value = to.dataset[name];
      if (value !== undefined) {
        from.dataset[name] = value;
      }
    }
  }

  /**
   * @param {HTMLElement} trigger
   * @param {string} variantId
   */
  async #fetchTriggerAvailabilities(trigger, variantId) {
    const productUrl = trigger.dataset.productUrl?.trim();
    const sectionId = trigger.dataset.sectionId?.trim();
    if (!productUrl || !sectionId) return;

    try {
      const response = await fetch(`${productUrl}?variant=${variantId}&section_id=${sectionId}`);
      const text = await response.text();
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const updated = doc.querySelector('[data-store-locator-trigger][data-store-locator-pdp]');
      if (updated instanceof HTMLElement) {
        this.#copyTriggerDataset(trigger, updated);
      }
    } catch {
      /* ignore fetch errors */
    }
  }

  /**
   * @returns {HTMLElement | null}
   */
  #findPdpTrigger() {
    return findPdpStoreLocatorTrigger();
  }

  /**
   * @param {{ item_count?: number }} cart
   */
  #dispatchCartUpdateEvent(cart) {
    dispatchStoreLocatorCartUpdate(this.id ?? 'store-locator-root', cart);
  }

  /**
   * @param {Record<string, string>} attributes
   * @param {{ dispatchCartUpdate?: boolean }} [options]
   * @returns {Promise<CartUpdateResult>}
   */
  async #patchCartAttributes(attributes, options = {}) {
    const { dispatchCartUpdate = true } = options;

    const result = await patchStoreCartAttributes(attributes, { dispatchCartUpdate: false });
    if (!result.ok) {
      return { ok: false, error: result.error };
    }

    const cart = result.cart;

    if (
      cart &&
      (!cart.attributes || !hasStoreCartAttributes(normalizeCartAttributes(cart.attributes) ?? {}))
    ) {
      console.warn('[store-locator] patch cart response missing store attributes', {
        requestedAttributes: attributes,
        responseAttributes: cart?.attributes,
      });
    }

    if (dispatchCartUpdate && cart) {
      this.#dispatchCartUpdateEvent(/** @type {{ item_count?: number }} */ (cart));
    }

    return { ok: true, cart };
  }

  /**
   * @returns {Promise<CartUpdateResult>}
   */
  async #clearStaleStoreCartAttributes() {
    console.warn('[store-locator] stale store attrs, clearing cart');
    return this.#patchCartAttributes(buildEmptyStoreCartAttributes());
  }

  async #hydrateSelectionFromCart() {
    const generation = ++this.#state.meta.cartSyncGeneration;
    const cart = /** @type {{ attributes?: Record<string, string> } | null} */ (await fetchCartJson());

    if (generation !== this.#state.meta.cartSyncGeneration) return;

    const fetchedAttrs = normalizeCartAttributes(cart?.attributes);
    if (hasStoreCartAttributes(fetchedAttrs ?? {})) {
      this.#state.cartAttributes = fetchedAttrs;
    } else if (!hasStoreCartAttributes(this.#state.cartAttributes ?? {})) {
      this.#state.cartAttributes = fetchedAttrs;
    }

    const catalog = this.#state.catalog;
    const attrs = this.#state.cartAttributes;

    if (!hasStoreCartAttributes(attrs ?? {})) {
      this.#state.selectedStore = null;
      return;
    }

    if (catalog.length === 0) {
      console.warn('[store-locator] store attrs present but catalog empty; skipping cart clear');
      this.#state.selectedStore = null;
      return;
    }

    if (
      !findStoreFromCartAttributes(
        /** @type {import('@theme/store-pricing').StoreCatalogEntry[]} */ (catalog),
        /** @type {Record<string, string>} */ (attrs)
      )
    ) {
      console.warn('[store-locator] hydrate: cart attrs do not match catalog', {
        cartAttributes: attrs,
      });
      const cleared = await this.#clearStaleStoreCartAttributes();
      if (generation !== this.#state.meta.cartSyncGeneration) return;
      if (!cleared.ok) {
        this.#state.selectedStore = null;
        return;
      }
      this.#state.cartAttributes =
        normalizeCartAttributes(
          /** @type {{ attributes?: Record<string, unknown> } | undefined} */ (cleared.cart)?.attributes
        ) ?? buildEmptyStoreCartAttributes();
      this.#state.selectedStore = null;
      return;
    }

    this.#state.selectedStore = resolveSelectedStore(catalog, attrs);
  }

  /**
   * @param {CartUpdateEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event.detail?.data?.source === STORE_LOCATOR_SOURCE) {
      // CartUpdateEvent is dispatched after #state is updated (see #selectStore).
      return;
    }

    const resource = /** @type {{ attributes?: Record<string, string> } | null} */ (event.detail?.resource);
    const attributes = resource?.attributes;

    if (attributes && typeof attributes === 'object' && Object.keys(attributes).length === 0) {
      return;
    }

    void this.#reconcileCartFromEvent();
  };

  async #reconcileCartFromEvent() {
    if (this.#selectInProgress) return;

    await this.#hydrateSelectionFromCart();
    this.#renderUi();
  }

  #resetSearch() {
    const queryInput = this.#getRef('queryInput');
    const resultsList = this.#getRef('resultsList');
    const distanceInput = this.#getRef('distanceInput');
    const distanceValue = this.#getRef('distanceValue');
    const distanceDetails = this.#getRef('distanceDetails');
    const noResultsText = this.#getRef('noResultsText');
    const defaultRadius = this.#state.config.defaultRadius;

    if (queryInput instanceof HTMLInputElement) {
      queryInput.value = '';
    }

    if (resultsList instanceof HTMLElement) {
      resultsList.replaceChildren();
    }

    this.#state.search = {
      query: '',
      zip: '',
      radius: '',
      results: [],
      uiState: 'idle',
      noResultsZip: '',
      noResultsRadius: '',
      noResultsMode: 'zip',
      origin: null,
      distancesByStoreId: {},
    };

    if (distanceInput instanceof HTMLInputElement) {
      distanceInput.value = defaultRadius;
    }

    if (distanceValue instanceof HTMLElement) {
      distanceValue.textContent = `${defaultRadius} mi.`;
    }

    if (distanceDetails instanceof HTMLDetailsElement) {
      distanceDetails.querySelectorAll('[data-store-locator-radius-option]').forEach((option) => {
        if (!(option instanceof HTMLElement)) return;
        const isSelected = option.dataset.value === defaultRadius;
        option.setAttribute('aria-selected', isSelected ? 'true' : 'false');
      });
      distanceDetails.open = false;
    }

    if (noResultsText instanceof HTMLElement) {
      noResultsText.textContent = '';
    }

    this.#hideError();
  }

  #renderUi({ refreshHeader = true } = {}) {
    this.#hydrateTriggers();
    this.#renderSelectedStore();
    this.#applySearchUiState();
    if (
      (this.#state.search.uiState === 'results' || this.#state.search.uiState === 'resultsMyStoreOnly') &&
      this.#state.search.results.length > 0
    ) {
      this.#renderResults(this.#state.search.results);
    }
    if (refreshHeader) {
      syncHeaderHeightVars();
      refreshHeaderLayout();
    }
    void this.#hydrateVisibleResultBadges();
  }

  #applySearchUiState() {
    const { uiState, noResultsZip, noResultsRadius, noResultsMode } = this.#state.search;
    const hasSelectedStore = Boolean(this.#state.selectedStore);

    if (uiState === 'noResults') {
      this.#setNoResultsMessage(noResultsZip, noResultsRadius, noResultsMode);
    }

    const emptyState = this.#getRef('emptyState');
    const resultsPanel = this.#getRef('resultsPanel');
    const noResultsPanel = this.#getRef('noResultsPanel');
    const selectedPanel = this.#getRef('selectedPanel');

    if (emptyState instanceof HTMLElement) {
      emptyState.hidden = uiState !== 'idle' || hasSelectedStore;
    }

    if (selectedPanel instanceof HTMLElement) {
      selectedPanel.hidden = !hasSelectedStore;
    }

    if (resultsPanel instanceof HTMLElement) {
      resultsPanel.hidden = uiState !== 'results' && uiState !== 'resultsMyStoreOnly';
    }

    if (noResultsPanel instanceof HTMLElement) {
      noResultsPanel.hidden = uiState !== 'noResults';
    }
  }

  /**
   * @returns {StoreLocatorStore | null}
   */
  #getSelectedCatalogStore() {
    const selected = this.#state.selectedStore;
    if (!selected) return null;

    const fromCatalog = this.#state.catalog.find((store) => storeIdsMatch(store.id, selected.id));
    if (fromCatalog) return fromCatalog;

    return {
      id: selected.id,
      handle: selected.handle,
      displayName: selected.displayName,
      storeCode: selected.storeCode,
      shopifyLocationId: selected.shopifyLocationId,
      phone: '',
      hours: [],
      address: {
        address1: selected.address1,
        zipcode: selected.zipcode,
      },
      zipsByRadius: {},
    };
  }

  #renderSelectedStore() {
    const selectedPanel = this.#getRef('selectedPanel');
    const selectedList = this.#getRef('selectedList');
    const resultTemplate = this.#getRef('resultTemplate');
    const store = this.#getSelectedCatalogStore();

    if (!(selectedList instanceof HTMLElement) || !(resultTemplate instanceof HTMLTemplateElement)) {
      return;
    }

    selectedList.replaceChildren();

    if (!store) {
      if (selectedPanel instanceof HTMLElement) {
        selectedPanel.hidden = true;
      }
      return;
    }

    const fragment = /** @type {DocumentFragment} */ (resultTemplate.content.cloneNode(true));
    const item = fragment.querySelector('.store-locator-modal__result');
    if (!(item instanceof HTMLElement)) return;

    const showDistanceOnSelected = this.#state.search.uiState === 'resultsMyStoreOnly';

    this.#populateResultItem(item, store, { forceSelected: true, showDistanceOnSelected });
    selectedList.appendChild(fragment);
  }

  /**
   * @param {HTMLElement | null} badgeEl
   * @param {'positive' | 'negative' | 'hidden'} mode
   * @param {string} label
   * @returns {'positive' | 'negative' | 'hidden'}
   */
  #setBadgeState(badgeEl, mode, label) {
    if (!(badgeEl instanceof HTMLElement)) return 'hidden';

    if (mode === 'hidden') {
      badgeEl.hidden = true;
      return 'hidden';
    }

    badgeEl.hidden = false;
    badgeEl.classList.toggle('store-locator-modal__result-badge--positive', mode === 'positive');
    badgeEl.classList.toggle('store-locator-modal__result-badge--negative', mode === 'negative');

    const labelEl = badgeEl.querySelector('[data-badge-label]');
    if (labelEl) labelEl.textContent = label;

    const positiveIcon = badgeEl.querySelector('.store-locator-modal__result-badge-icon--positive');
    const negativeIcon = badgeEl.querySelector('.store-locator-modal__result-badge-icon--negative');
    if (positiveIcon instanceof HTMLElement) positiveIcon.hidden = mode !== 'positive';
    if (negativeIcon instanceof HTMLElement) negativeIcon.hidden = mode !== 'negative';

    return mode;
  }

  /**
   * @param {HTMLElement} item
   * @param {StoreLocatorBadgeState | null} badgeState
   */
  #applyBadgesToItem(item, badgeState) {
    const inStockLabel = Theme.translations.store_locator_in_stock_in_store ?? '';
    const pickupLabel = Theme.translations.store_locator_pickup_in_store ?? '';
    const outOfStockLabel = Theme.translations.store_locator_out_of_stock_in_store ?? '';
    const pickupUnavailableLabel = Theme.translations.store_locator_not_available_pickup_in_store ?? '';
    const badgesWrapper = item.querySelector('.store-locator-modal__result-badges');
    const inStockBadge = item.querySelector('[data-badge="in-stock"]');
    const pickupBadge = item.querySelector('[data-badge="pickup"]');

    if (!(badgesWrapper instanceof HTMLElement)) return;

    if (!badgeState) {
      badgesWrapper.hidden = true;
      this.#setBadgeState(inStockBadge instanceof HTMLElement ? inStockBadge : null, 'hidden', '');
      this.#setBadgeState(pickupBadge instanceof HTMLElement ? pickupBadge : null, 'hidden', '');
      return;
    }

    /** @type {'positive' | 'negative' | 'hidden'} */
    let stockMode = 'hidden';
    if (badgeState.inStock) {
      stockMode = 'positive';
    } else {
      stockMode = 'negative';
    }

    const pickupMode = badgeState.pickup ? 'positive' : 'negative';

    const stockResult = this.#setBadgeState(
      inStockBadge instanceof HTMLElement ? inStockBadge : null,
      stockMode,
      stockMode === 'positive' ? inStockLabel : outOfStockLabel
    );
    const pickupResult = this.#setBadgeState(
      pickupBadge instanceof HTMLElement ? pickupBadge : null,
      pickupMode,
      pickupMode === 'positive' ? pickupLabel : pickupUnavailableLabel
    );

    badgesWrapper.hidden = stockResult === 'hidden' && pickupResult === 'hidden';
  }

  /**
   * @param {string} storeId
   * @param {StoreLocatorBadgeState} badgeState
   */
  #updateBadgesForStoreId(storeId, badgeState) {
    const selector = `.store-locator-modal__result[data-store-id="${CSS.escape(storeId)}"]`;
    document.querySelectorAll(selector).forEach((node) => {
      if (node instanceof HTMLElement) {
        this.#applyBadgesToItem(node, badgeState);
      }
    });
  }

  #hydrateVisibleResultBadges() {
    if (!this.#usesInventoryBadges()) return;

    /** @type {StoreLocatorStore[]} */
    const stores = [];
    const selected = this.#getSelectedCatalogStore();
    if (selected) stores.push(selected);

    if (this.#state.search.results.length > 0) {
      const selectedId = this.#state.selectedStore?.id ?? null;
      const listStores = selectedId
        ? this.#state.search.results.filter((store) => !storeIdsMatch(selectedId, store.id))
        : this.#state.search.results;
      stores.push(...listStores);
    }

    if (stores.length === 0) return;

    void this.#hydrateResultBadges(stores);
  }

  #refreshPdpBadgesOnModalOpen() {
    if (!this.#state.pdpContext) return;

    this.#renderSelectedStore();

    const { uiState } = this.#state.search;
    if (
      (uiState === 'results' || uiState === 'resultsMyStoreOnly') &&
      this.#state.search.results.length > 0
    ) {
      this.#renderResults(this.#state.search.results);
    }

    void this.#hydrateVisibleResultBadges();
  }

  /**
   * @param {StoreLocatorStore[]} stores
   */
  async #hydrateResultBadges(stores) {
    const pdpContext = this.#state.pdpContext;
    if (!pdpContext || !this.#usesInventoryBadges()) return;

    this.#inventoryFetchAbort?.abort();
    this.#inventoryFetchAbort = new AbortController();
    const { signal } = this.#inventoryFetchAbort;
    const requestGeneration = ++this.#inventoryRequestGeneration;
    const storeIds = stores.map((store) => store.id);

    this.#setBadgesHydrating(storeIds, true);

    try {
      const {
        variantId,
        inventoryApiBase,
        inventoryThreshold,
        availabilitiesByLocationId,
        inventoryPolicy,
        inStockInStoreValues,
      } = pdpContext;

      for (const store of stores) {
        if (requestGeneration !== this.#inventoryRequestGeneration) return;

        const locationId = store.shopifyLocationId?.trim();
        if (!locationId) {
          this.#updateBadgesForStoreId(store.id, this.#resolveBadgeStateForStore(store) ?? {
            inStock: false,
            pickup: false,
            status: 'unavailable',
            pickUpEnabled: false,
          });
          continue;
        }

        const cacheKey = `${variantId}:${normalizeLocationId(locationId)}`;
        const cached = this.#inventoryBadgeCache.get(cacheKey);

        if (cached) {
          this.#updateBadgesForStoreId(store.id, cached);
          continue;
        }

        const pickUpEnabled = this.#getPickUpEnabled(store, pdpContext);
        const allowContinueSelling = allowsContinueSellingAtStore({
          inventoryPolicy,
          inStockInStoreValues,
          storeCode: store.storeCode,
        });
        const availability = await resolveStoreInventoryForLocation({
          baseUrl: inventoryApiBase,
          authToken: pdpContext.inventoryApiAuthToken,
          variantId,
          locationId,
          availabilitiesByLocationId,
          threshold: inventoryThreshold,
          pickUpEnabled,
          allowContinueSelling,
          signal,
        });

        if (requestGeneration !== this.#inventoryRequestGeneration) return;

        const badgeState = availabilityToBadgeState(availability);
        this.#inventoryBadgeCache.set(cacheKey, badgeState);
        this.#updateBadgesForStoreId(store.id, badgeState);
      }
    } finally {
      if (requestGeneration === this.#inventoryRequestGeneration) {
        this.#setBadgesHydrating(storeIds, false);
      }
    }
  }

  /**
   * @param {HTMLElement} item
   * @param {StoreLocatorStore} store
   * @param {{
   *   forceSelected?: boolean;
   *   showDistanceOnSelected?: boolean;
   *   distancesByStoreId?: Record<string, number>;
   *   origin?: StoreLocatorSearchOrigin | null;
   *   searchQuery?: string;
   *   onSelectStore?: (store: StoreLocatorStore, button: HTMLButtonElement) => void;
   * }} [options]
   */
  #populateResultItem(item, store, options = {}) {
    const {
      forceSelected = false,
      showDistanceOnSelected = false,
      distancesByStoreId,
      origin,
      searchQuery,
      onSelectStore,
    } = options;

    const distanceMap = distancesByStoreId ?? this.#state.search.distancesByStoreId;
    const searchOrigin = origin !== undefined ? origin : this.#state.search.origin;
    const query = searchQuery ?? this.#state.search.query;
    const distancePlaceholder = Theme.translations.store_locator_distance_placeholder ?? '—';
    const setStoreLabel = Theme.translations.store_locator_set_as_my_store ?? '';
    const showBadges = Boolean(this.#state.pdpContext);
    const selectedId = this.#state.selectedStore?.id ?? null;

    item.dataset.storeId = store.id;

    const nameEl = item.querySelector('.store-locator-modal__result-name');
    if (nameEl) nameEl.textContent = store.displayName || store.handle;

    const subtitleEl = item.querySelector('.store-locator-modal__result-subtitle');
    const city = store.address?.city?.trim();
    if (subtitleEl instanceof HTMLElement) {
      if (city) {
        subtitleEl.textContent = city;
        subtitleEl.hidden = false;
      } else {
        subtitleEl.hidden = true;
      }
    }

    const address = store.address ?? {};
    const line1 = item.querySelector('[data-address-line="1"]');
    const line2 = item.querySelector('[data-address-line="2"]');
    const cityLine = item.querySelector('[data-address-line="city"]');

    if (line1 instanceof HTMLElement) {
      if (address.address1) {
        line1.textContent = address.address1;
        line1.hidden = false;
      } else {
        line1.hidden = true;
      }
    }

    if (line2 instanceof HTMLElement) {
      if (address.address2) {
        line2.textContent = address.address2;
        line2.hidden = false;
      } else {
        line2.hidden = true;
      }
    }

    if (cityLine instanceof HTMLElement) {
      const cityState = [address.city, address.state].filter(Boolean).join(', ');
      const cityStateZip = [cityState, address.zipcode].filter(Boolean).join(' ').trim();
      if (cityStateZip) {
        cityLine.textContent = cityStateZip;
        cityLine.hidden = false;
      } else {
        cityLine.hidden = true;
      }
    }

    const distanceEl = item.querySelector('.store-locator-modal__result-distance');
    if (distanceEl instanceof HTMLElement) {
      const showDistance = !forceSelected || showDistanceOnSelected;
      distanceEl.hidden = !showDistance;

      if (showDistance) {
        const miles = distanceMap[store.id];

        if (Number.isFinite(miles)) {
          const label = getOriginLabelForDisplay(searchOrigin, query);
          const template =
            Theme.translations.store_locator_distance_from_label ??
            Theme.translations.store_locator_distance_from ??
            '{{ distance }} mi from {{ label }}';

          distanceEl.textContent = template
            .replace(/\{\{\s*distance\s*\}\}/g, formatDistanceMiles(/** @type {number} */ (miles)))
            .replace(/\{\{\s*label\s*\}\}/g, label)
            .replace(/\{\{\s*zip\s*\}\}/g, label);
          distanceEl.hidden = false;
        } else {
          distanceEl.textContent = distancePlaceholder;
          distanceEl.hidden = true;
        }
      }
    }

    const phoneWrapper = item.querySelector('.store-locator-modal__result-phone');
    const phoneLink = item.querySelector('.store-locator-modal__result-phone-link');
    const phone = store.phone?.trim();

    if (phoneWrapper instanceof HTMLElement && phoneLink instanceof HTMLAnchorElement) {
      if (phone) {
        const tel = phone.replace(/[^\d+]/g, '');
        phoneLink.href = `tel:${tel}`;
        phoneLink.textContent = this.#formatPhoneDisplay(phone);
        phoneWrapper.hidden = false;
      } else {
        phoneWrapper.hidden = true;
      }
    }

    const hoursBlock = item.querySelector('.store-locator-modal__result-hours');
    const hoursLines = item.querySelector('.store-locator-modal__result-hours-lines');
    const hours = store.hours ?? [];

    if (hoursBlock instanceof HTMLElement && hoursLines instanceof HTMLElement) {
      hoursLines.replaceChildren();
      if (hours.length > 0) {
        for (const line of hours) {
          const p = document.createElement('p');
          p.className = 'store-locator-modal__result-hours-line';
          p.textContent = line;
          hoursLines.appendChild(p);
        }
        hoursBlock.hidden = false;
      } else {
        hoursBlock.hidden = true;
      }
    }

    const badgesWrapper = item.querySelector('.store-locator-modal__result-badges');

    if (badgesWrapper instanceof HTMLElement) {
      if (showBadges) {
        this.#applyBadgesToItem(item, this.#resolveBadgeStateForStore(store));
      } else {
        this.#applyBadgesToItem(item, null);
      }
    }

    const setStoreButton = item.querySelector('[data-store-locator-set-store]');
    const isSelected =
      forceSelected || (selectedId !== null && storeIdsMatch(selectedId, store.id));

    if (setStoreButton instanceof HTMLButtonElement) {
      setStoreButton.hidden = isSelected;
      if (!isSelected) {
        setStoreButton.textContent = setStoreLabel;
        setStoreButton.removeAttribute('aria-pressed');
        setStoreButton.disabled = false;

        if (onSelectStore) {
          setStoreButton.addEventListener('click', (event) => {
            event.preventDefault();
            onSelectStore(store, setStoreButton);
          });
        }
      }
    }

    const actionsWrapper = item.querySelector('.store-locator-modal__result-actions');
    if (actionsWrapper instanceof HTMLElement) {
      const badgesVisible =
        badgesWrapper instanceof HTMLElement && !badgesWrapper.hidden;
      const buttonVisible =
        setStoreButton instanceof HTMLButtonElement && !setStoreButton.hidden;
      actionsWrapper.hidden = !badgesVisible && !buttonVisible;
    }
  }

  /**
   * @param {string} location
   * @param {string} radius
   * @param {'zip' | 'cityState' | 'state'} [mode]
   */
  #setNoResultsMessage(location, radius, mode = 'zip') {
    const noResultsText = this.#getRef('noResultsText');
    if (!(noResultsText instanceof HTMLElement)) return;

    const template =
      mode === 'state'
        ? (Theme.translations.store_locator_no_results_state ?? '')
        : mode === 'cityState'
          ? (Theme.translations.store_locator_no_results_city_state ?? '')
          : (Theme.translations.store_locator_no_results_zip ?? '');

    noResultsText.textContent = template
      .replace(/\{\{\s*location\s*\}\}/g, location)
      .replace(/\{\{\s*zip\s*\}\}/g, location)
      .replace(/\{\{\s*radius\s*\}\}/g, radius);
  }

  /**
 * @param {StoreLocatorStore} store
 * @param {HTMLButtonElement} button
 */
async #selectStore(store, button) {
  console.log('[store-locator] Select store started', {
    store,
    button,
    buttonType: button.matches(
      '[data-store-page-set-store]'
    )
      ? 'store-page'
      : 'locator-result'
  });

  const isLocatorResultButton = button.matches(
    '[data-store-locator-set-store]'
  );

  /*
   * The result-selection guard is only intended to prevent
   * accidental clicks inside the locator modal immediately
   * after search results render. It should not block the
   * standalone button on a Store Location page.
   */
  if (
    isLocatorResultButton &&
    Date.now() < this.#blockResultSelectionUntil
  ) {
    console.warn(
      '[store-locator] Locator result selection temporarily blocked'
    );

    return;
  }

  if (this.#selectInProgress) {
    console.warn(
      '[store-locator] Store selection already in progress'
    );

    return;
  }

  button.disabled = true;
  button.setAttribute('aria-busy', 'true');

  this.#selectInProgress = true;
  this.#setSelectLoading(true);

  try {
    const requestedAttributes =
      buildCartAttributes(store);

    console.log(
      '[store-locator] Updating cart attributes',
      requestedAttributes
    );

    const result = await this.#patchCartAttributes(
      requestedAttributes,
      {
        dispatchCartUpdate: false
      }
    );

    console.log(
      '[store-locator] Cart attribute update result',
      result
    );

    if (!result.ok) {
      console.error(
        '[store-locator] Cart attribute update failed',
        result.error
      );

      this.#showError(
        Theme.translations
          .store_locator_set_store_error ?? ''
      );

      return;
    }

    const cart =
      /** @type {{
       * attributes?: Record<string, unknown>
       * } | undefined} */
      (result.cart);

    this.#state.cartAttributes =
      normalizeCartAttributes(cart?.attributes);

    this.#state.selectedStore =
      resolveSelectedStore(
        this.#state.catalog,
        this.#state.cartAttributes
      );

    console.log(
      '[store-locator] Selected store after cart update',
      {
        cartAttributes: this.#state.cartAttributes,
        selectedStore: this.#state.selectedStore
      }
    );

    if (!this.#state.selectedStore) {
      console.warn(
        '[store-locator] Cart update succeeded but selected store was not persisted/resolved',
        {
          requestedAttributes,
          responseAttributes:
            this.#state.cartAttributes,
          store
        }
      );

      this.#showError(
        Theme.translations
          .store_locator_set_store_error ?? ''
      );

      return;
    }

    this.#state.meta.cartSyncGeneration++;
    this.#hideError();

    this.#dispatchCartUpdateEvent(
      /** @type {{ item_count?: number }} */
      (cart)
    );

    document.dispatchEvent(
      new CustomEvent(STORE_SELECTED_EVENT, {
        bubbles: true,
        detail: { store }
      })
    );

    this.#scheduleHeaderUiRefresh();

    if (this.dialog) {
      await this.dialog.closeDialog();
    }

    this.#scheduleHeaderUiRefresh();

    console.log(
      '[store-locator] Store selection completed successfully',
      this.#state.selectedStore
    );
  } catch (error) {
    console.error(
      '[store-locator] Unexpected store selection error',
      error
    );

    this.#showError(
      Theme.translations
        .store_locator_set_store_error ?? ''
    );

    throw error;
  } finally {
    this.#selectInProgress = false;
    this.#setSelectLoading(false);

    button.removeAttribute('aria-busy');

    const selectedLocationId =
      this.#state.selectedStore
        ?.shopifyLocationId
        ?.trim() ?? '';

    const buttonLocationId =
      button.dataset.storeId?.trim() ?? '';

    const isCurrentStore =
      Boolean(buttonLocationId) &&
      Boolean(selectedLocationId) &&
      normalizeLocationId(buttonLocationId) ===
        normalizeLocationId(selectedLocationId);

    button.disabled = isCurrentStore;

    console.log(
      '[store-locator] Select store finished',
      {
        isCurrentStore,
        selectedLocationId,
        buttonLocationId
      }
    );
  }
}

  #hydrateTriggers() {
    const selected = this.#state.selectedStore;
    const myStoreTemplate = Theme.translations.store_locator_my_store_label ?? '';
    const pdpTemplate = Theme.translations.store_locator_your_store_label ?? '';

    document.querySelectorAll('[data-store-page-set-store]').forEach((button) => {
  if (!(button instanceof HTMLButtonElement)) return;

  const pageLocationId = button.dataset.storeId?.trim() ?? '';
  const selectedLocationId = selected?.shopifyLocationId?.trim() ?? '';

  const isCurrentStore =
    Boolean(pageLocationId) &&
    Boolean(selectedLocationId) &&
    normalizeLocationId(pageLocationId) === normalizeLocationId(selectedLocationId);

  button.classList.toggle('is-current-store', isCurrentStore);
  button.setAttribute('aria-pressed', isCurrentStore ? 'true' : 'false');
  button.disabled = isCurrentStore;

  const label = button.querySelector('.af-store-action__label');

  if (label instanceof HTMLElement) {
    label.textContent = isCurrentStore ? 'My Store' : 'Set as My Store';
  }
});

    document.querySelectorAll('[data-store-locator-header]').forEach((trigger) => {
      if (!(trigger instanceof HTMLElement)) return;

      const defaultBlock = trigger.querySelector('.store-locator__default');
      const selectedBlock = trigger.querySelector('.store-locator__selected');
      const nameEl = trigger.querySelector('.store-locator__store-name');
      const addressEl = trigger.querySelector('.store-locator__store-address');

      if (selected?.displayName) {
        if (defaultBlock instanceof HTMLElement) defaultBlock.hidden = true;
        if (selectedBlock instanceof HTMLElement) selectedBlock.hidden = false;
        if (nameEl instanceof HTMLElement) {
          nameEl.textContent = myStoreTemplate.replace(/\{\{\s*store_name\s*\}\}/g, selected.displayName);
        }
        if (addressEl instanceof HTMLElement) {
          addressEl.textContent = selected.address1?.trim() ?? '';
          addressEl.hidden = !addressEl.textContent;
        }
        trigger.setAttribute(
          'aria-label',
          `${selected.displayName}${selected.address1 ? `, ${selected.address1}` : ''}`
        );
        trigger.classList.add('store-locator__button--selected');
      } else {
        if (defaultBlock instanceof HTMLElement) defaultBlock.hidden = false;
        if (selectedBlock instanceof HTMLElement) selectedBlock.hidden = true;
        trigger.setAttribute('aria-label', Theme.translations.store_locator_heading ?? 'Find My Store');
        trigger.classList.remove('store-locator__button--selected');
      }
    });

    document.querySelectorAll('[data-store-locator-trigger][data-store-locator-pdp]').forEach((trigger) => {
      if (!(trigger instanceof HTMLElement)) return;
      if (isStoreInventoryPdpTrigger(trigger)) return;

      const textEl = trigger.querySelector('.store-availability__text');
      if (!(textEl instanceof HTMLElement)) return;

      const defaultLabel = trigger.dataset.defaultLabel?.trim();
      if (!defaultLabel && trigger.dataset.defaultLabel === undefined) {
        trigger.dataset.defaultLabel = textEl.textContent?.trim() ?? '';
      }

      if (selected?.displayName) {
        textEl.textContent = pdpTemplate.replace(/\{\{\s*store_name\s*\}\}/g, selected.displayName);
      } else {
        textEl.textContent = trigger.dataset.defaultLabel ?? textEl.textContent;
      }
    });
  }

  /**
   * @param {StoreLocatorStore[]} stores
   */
  #renderResults(stores) {
    const resultsCount = this.#getRef('resultsCount');
    const resultsList = this.#getRef('resultsList');
    const resultTemplate = this.#getRef('resultTemplate');

    if (!(resultsList instanceof HTMLElement) || !(resultTemplate instanceof HTMLTemplateElement)) {
      return;
    }

    this.#state.search.results = stores;
    resultsList.replaceChildren();

    const selectedId = this.#state.selectedStore?.id ?? null;
    const listStores = selectedId
      ? stores.filter((store) => !storeIdsMatch(selectedId, store.id))
      : stores;

    const count = listStores.length;
    if (resultsCount instanceof HTMLElement) {
      if (count === 0 && stores.length > 0) {
        resultsCount.textContent = Theme.translations.store_locator_results_my_store_only ?? '';
      } else {
        const one = Theme.translations.store_locator_stores_found_one ?? '';
        const other = Theme.translations.store_locator_stores_found_other ?? '';
        const templateStr = count === 1 ? one : other;
        resultsCount.textContent = templateStr.replace(/\{\{\s*count\s*\}\}/g, String(count));
      }
    }

    if (count === 0) {
      return;
    }

    for (const store of listStores) {
      const fragment = /** @type {DocumentFragment} */ (resultTemplate.content.cloneNode(true));
      const item = fragment.querySelector('.store-locator-modal__result');
      if (!(item instanceof HTMLElement)) continue;

      this.#populateResultItem(item, store);
      resultsList.appendChild(fragment);
    }
  }

  /**
   * @param {string} message
   */
  /**
   * @returns {string}
   */
  #getGeocodingErrorMessage() {
    if (isMapsApiAuthFailed()) {
      return Theme.translations.store_locator_maps_api_denied ?? '';
    }

    return Theme.translations.store_locator_geocoding_failed ?? '';
  }

  /**
   * @param {string} message
   */
  #showError(message) {
    const errorMessage = this.#getRef('errorMessage');
    if (!(errorMessage instanceof HTMLElement)) return;

    errorMessage.textContent = message;
    errorMessage.hidden = false;
  }

  #hideError() {
    const errorMessage = this.#getRef('errorMessage');
    if (!(errorMessage instanceof HTMLElement)) return;

    errorMessage.textContent = '';
    errorMessage.hidden = true;
  }

  /**
   * @param {string} phone
   * @returns {string}
   */
  #formatPhoneDisplay(phone) {
    const digits = phone.replace(/[^\d+]/g, '');

    if (digits.length === 10) {
      return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
    }

    if (digits.length === 11) {
      return `+1-${digits.slice(1, 4)}-${digits.slice(4, 7)}-${digits.slice(7)}`;
    }

    return digits.trim();
  }
}

if (!customElements.get('store-locator')) {
  customElements.define('store-locator', StoreLocator);
}

export { StoreLocator };
